/**
 * Demo Wallet Deposit Modal
 *
 * 모의거래 지갑 입금 모달
 * - 입금 금액 입력
 * - 최대 한도 안내 (1,000,000 USDT)
 * - 입금 후 잔액 자동 업데이트
 *
 * @see coin-server/apps/api/src/demo/handlers/demo-wallet-deposit.handler.ts
 */

import React, { useState, useContext } from 'react';
import { Modal, Button, InputNumber, message, Spin } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useIsTablet } from '@utils/CalVW.tsx';
import { getBigNumber } from '@utils/getBigNumber';
import { formatDecimal } from '@utils/formatDecimal';
import { useDemoTradingStore } from '@app/hooks/trade/demoTradingStore';
import { demoWalletApi } from '@app/api/demoWallet';
import { useDemoWallet } from '@app/hooks/wallet/useUnifiedWallet';

interface DemoWalletDepositModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

export const DemoWalletDepositModal: React.FC<DemoWalletDepositModalProps> = ({
  open,
  setOpen,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const { isDarkMode } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';

  const { account, updateBalance } = useDemoTradingStore();
  const { data: demoWallet, mutate: refreshUnifiedWallet } = useDemoWallet(); // ✅ data 추가

  const [depositAmount, setDepositAmount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  // 최대 한도 (환경변수에서 설정 가능, 기본 1,000,000 USDT)
  const MAX_BALANCE = 1000000;

  const handleCancel = () => {
    setOpen(false);
    setDepositAmount(0);
  };

  const handleDeposit = async () => {
    if (depositAmount <= 0) {
      message.error(t('demo.depositAmountRequired') || '입금 금액을 입력하세요');
      return;
    }

    if (depositAmount < 1) {
      message.error(t('demo.minimumDepositAmount') || '최소 입금 금액은 1 USDT입니다');
      return;
    }

    const currentBalance = getBigNumber(account.balance);
    const newBalance = currentBalance.plus(depositAmount);

    // 최대 한도 체크
    if (newBalance.isGreaterThan(MAX_BALANCE)) {
      const maxDeposit = getBigNumber(MAX_BALANCE).minus(currentBalance);
      message.error(
        t('demo.maxBalanceExceeded', {
          maxBalance: MAX_BALANCE.toLocaleString(),
          maxDeposit: maxDeposit.toFixed(2),
        }) ||
          `최대 잔액 ${MAX_BALANCE.toLocaleString()} USDT를 초과할 수 없습니다. 최대 입금 가능: ${maxDeposit.toFixed(2)} USDT`
      );
      return;
    }

    setIsLoading(true);
    try {
      // API 호출
      const result = await demoWalletApi.depositToFutures(depositAmount);

      // Zustand 스토어 업데이트
      updateBalance(result.data.balance);

      // Unified Wallet 캐시 갱신 및 강제 재검증 (wallet/unified 재호출)
      await refreshUnifiedWallet(undefined, { revalidate: true });

      message.success(
        t('demo.depositSuccess', { amount: depositAmount }) ||
          `${depositAmount.toLocaleString()} USDT가 입금되었습니다`
      );

      setDepositAmount(0);

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        setOpen(false);
      }, 1000);
    } catch (error: any) {
      console.error('Demo wallet deposit error:', error);
      message.error(error.message || t('demo.depositFailed') || '입금에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePresetAmount = (amount: number) => {
    setDepositAmount(amount);
  };

  // 현재 잔액 계산 (Unified Wallet 우선, fallback은 Zustand)
  const currentBalance = getBigNumber(demoWallet?.balance || account.balance || '0').toNumber();

  // 최대 입금 가능 금액
  const maxDepositAllowed = getBigNumber(MAX_BALANCE).minus(currentBalance).toNumber();

  return (
    <Modal
      open={open}
      width={isTablet ? '90%' : '480px'}
      centered={true}
      closable={true}
      onCancel={handleCancel}
      footer={[
        <Button key="back" onClick={handleCancel} disabled={isLoading}>
          {t('cancel') || '취소'}
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={handleDeposit}>
          {t('demo.deposit') || '입금'}
        </Button>,
      ]}
      title={
        <ModalTitle isDarkMode={isDarkMode}>
          {t('demo.walletDeposit') || '모의거래 지갑 입금'}
        </ModalTitle>
      }
    >
      <ModalContent>
        {/* 현재 잔액 */}
        <BalanceSection isDarkMode={isDarkMode}>
          <BalanceLabel>{t('demo.currentBalance') || '현재 잔액'}</BalanceLabel>
          <BalanceValue>{currentBalance.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} USDT</BalanceValue>
        </BalanceSection>

        {/* 입금 금액 입력 */}
        <InputSection>
          <InputLabel isDarkMode={isDarkMode}>
            {t('demo.depositAmount') || '입금 금액'}
          </InputLabel>
          <InputWrapper>
            <StyledInputNumber
              value={depositAmount}
              onChange={(value) => setDepositAmount(typeof value === 'number' ? value : 0)}
              min={1}
              max={maxDepositAllowed}
              precision={0}
              step={1}
              placeholder="0"
              disabled={isLoading}
              addonAfter="USDT"
              style={{ width: '100%' }}
            />
          </InputWrapper>
        </InputSection>

        {/* 프리셋 금액 버튼 */}
        <PresetSection>
          <PresetButton
            onClick={() => handlePresetAmount(100)}
            disabled={isLoading || maxDepositAllowed < 100}
            isDarkMode={isDarkMode}
          >
            +100
          </PresetButton>
          <PresetButton
            onClick={() => handlePresetAmount(1000)}
            disabled={isLoading || maxDepositAllowed < 1000}
            isDarkMode={isDarkMode}
          >
            +1,000
          </PresetButton>
          <PresetButton
            onClick={() => handlePresetAmount(10000)}
            disabled={isLoading || maxDepositAllowed < 10000}
            isDarkMode={isDarkMode}
          >
            +10,000
          </PresetButton>
          <PresetButton
            onClick={() => handlePresetAmount(maxDepositAllowed > 0 ? maxDepositAllowed : 0)}
            disabled={isLoading || maxDepositAllowed <= 0}
            isDarkMode={isDarkMode}
          >
            {t('demo.max') || '최대'}
          </PresetButton>
        </PresetSection>

        {/* 입금 후 예상 잔액 */}
        <SummarySection isDarkMode={isDarkMode}>
          <SummaryRow>
            <SummaryLabel>{t('demo.afterDeposit') || '입금 후 잔액'}</SummaryLabel>
            <SummaryValue>
              {getBigNumber(currentBalance).plus(depositAmount).toNumber().toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} USDT
            </SummaryValue>
          </SummaryRow>
        </SummarySection>

        {/* 안내 메시지 */}
        <NoticeBox isDarkMode={isDarkMode}>
          <NoticeTitle>{t('demo.notice') || '안내사항'}</NoticeTitle>
          <NoticeItem>• {t('demo.noticeMinAmount') || '최소 입금: 1 USDT'}</NoticeItem>
          <NoticeItem>
            • {t('demo.noticeMaxBalance') || `최대 잔액: ${MAX_BALANCE.toLocaleString()} USDT`}
          </NoticeItem>
          <NoticeItem>
            •{' '}
            {t('demo.noticeMaxDeposit') ||
              `최대 입금 가능: ${maxDepositAllowed.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} USDT`}
          </NoticeItem>
          <NoticeItem>
            • {t('demo.noticeDemoMode') || '모의거래 전용 기능으로 실제 자금이 이동하지 않습니다'}
          </NoticeItem>
        </NoticeBox>
      </ModalContent>
    </Modal>
  );
};

// Styled Components
const ModalTitle = styled.div<{ isDarkMode: boolean }>`
  color: ${(props) => (props.isDarkMode ? '#ffffff' : '#000000')};
  font-size: 18px;
  font-weight: 600;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 12px 0;
`;

const BalanceSection = styled.div<{ isDarkMode: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: ${(props) => (props.isDarkMode ? '#1f1f1f' : '#f5f5f5')};
  border-radius: 8px;
`;

const BalanceLabel = styled.div`
  font-size: 14px;
  color: #888;
`;

const BalanceValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #4caf50;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputLabel = styled.div<{ isDarkMode: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.isDarkMode ? '#ffffff' : '#000000')};
`;

const InputWrapper = styled.div`
  width: 100%;
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  font-size: 16px;
`;

const PresetSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const PresetButton = styled.button<{ isDarkMode: boolean }>`
  padding: 10px;
  border: 1px solid ${(props) => (props.isDarkMode ? '#444' : '#d9d9d9')};
  background: ${(props) => (props.isDarkMode ? '#2a2a2a' : '#ffffff')};
  color: ${(props) => (props.isDarkMode ? '#ffffff' : '#000000')};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${(props) => (props.isDarkMode ? '#3a3a3a' : '#f0f0f0')};
    border-color: #1890ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummarySection = styled.div<{ isDarkMode: boolean }>`
  padding: 16px;
  background: ${(props) =>
    props.isDarkMode ? 'rgba(24, 144, 255, 0.1)' : 'rgba(24, 144, 255, 0.05)'};
  border-radius: 8px;
  border: 1px solid ${(props) => (props.isDarkMode ? '#1890ff' : '#91d5ff')};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  color: #888;
`;

const SummaryValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1890ff;
`;

const NoticeBox = styled.div<{ isDarkMode: boolean }>`
  padding: 16px;
  background: ${(props) =>
    props.isDarkMode ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.05)'};
  border-radius: 8px;
  border: 1px solid ${(props) => (props.isDarkMode ? '#ffc107' : '#ffe58f')};
`;

const NoticeTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ffc107;
  margin-bottom: 8px;
`;

const NoticeItem = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }
`;
